import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useId, useMemo } from 'react'

import { ProductPropertyDS } from '@src/components/blocks/product-property-ds'
import { getProductLinkWithSlug } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { AddOnProductOptionGroup } from '@src/quince-site-common/src/domains/AddOnProductOptionGroup'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { getDisplayKey } from '@src/utils/i18n.utils'

import SiblingProductButtonDS from './sibling-product-button-ds'
import { optionRowDsClass } from './sibling-product-options-ds.styles'

export enum SiblingProductOptionType {
  LINK = 'LINK',
  BUTTON = 'BUTTON',
}

interface SiblingProductOptionsProps {
  siblingProductOptionGroup: AddOnProductOptionGroup
  displayType: SiblingProductOptionType
  selectedProductOptions?: ProductVariantOption[]
  onSelect?: (val: string, productId?: number) => void
  selectedProductId?: number
  className?: string
  labelClassName?: string
  optionSelectorClassName?: string
  optionClassname?: string
  isMiniPDP?: boolean
}

export default function SiblingProductOptionsDS({
  siblingProductOptionGroup,
  onSelect,
  displayType,
  selectedProductId,
  selectedProductOptions = [],
  className,
  labelClassName,
  optionSelectorClassName,
  optionClassname,
  isMiniPDP,
}: SiblingProductOptionsProps) {
  const isNewLocalisedFlow = useI18nPreviewContext()
  // No longer used for the header id / aria-labelledby wiring — ProductPropertyDS owns that
  // internally (its own useId). Kept only as a unique prefix for each sibling button's id.
  const labelId = useId()

  const selectedGroupItemLabel = useMemo(() => {
    const selectedOption = siblingProductOptionGroup.groupItems.find(groupItem => groupItem.spid === selectedProductId)
    return selectedOption
      ? getDisplayKey(
          selectedOption,
          'mergeOptionValueDisplayName',
          selectedOption.mergeOptionValue,
          isNewLocalisedFlow,
        )
      : ''
  }, [siblingProductOptionGroup.groupItems, selectedProductId, isNewLocalisedFlow])

  return (
    <div data-testid="sibling-product-options">
      <ProductPropertyDS
        as="div"
        className={className}
        headerClassName={labelClassName}
        size={isMiniPDP ? 'sm' : 'md'}
        label={getDisplayKey(
          siblingProductOptionGroup,
          'groupOptionDisplayName',
          siblingProductOptionGroup.groupOptionName,
          isNewLocalisedFlow,
        )}
        selectedValue={selectedGroupItemLabel || undefined}
        contentClassName={cx(optionRowDsClass, optionSelectorClassName)}
      >
        {siblingProductOptionGroup.groupItems.map((groupItem, idx) => {
          return (
            <SiblingProductButtonDS
              id={`sibling-product-option-${labelId}-${idx}`}
              testId={`sibling-product-option-${idx}`}
              key={groupItem.spid}
              value={
                displayType === SiblingProductOptionType.LINK
                  ? getProductLinkWithSlug(groupItem.productHandle, selectedProductOptions, {
                      includeSize: true,
                      isMiniPDP,
                    })
                  : String(groupItem.spid)
              }
              displayName={getDisplayKey(
                groupItem,
                'mergeOptionValueDisplayName',
                groupItem.mergeOptionValue,
                isNewLocalisedFlow,
              )}
              isActive={groupItem.spid === selectedProductId}
              variant={displayType}
              onClickCallback={val => onSelect?.(val, groupItem.productId)}
              className={optionClassname}
            />
          )
        })}
      </ProductPropertyDS>
    </div>
  )
}
